/**
 * useBlockedShipments - Hook para obtener shipments bloqueados
 * 
 * Carga shipments bloqueados paginados desde GET /invoices/shipments/block con infinite scroll.
 * Incluye soporte para filtros avanzados, búsqueda, sorting.
 * Usa React Query con placeholderData para mantener datos previos visibles durante el refetch.
 * Diseñado para trabajar con AG Grid y paginación infinita.
 * 
 * @param {Filters} filters - Filtros aplicados (búsqueda, sorting, etc.)
 * @returns {Object} { blockedShipments, isLoading, isFetching, error, refetch, fetchNextPage, hasNextPage, total }
 */

import { createHttpClient } from '@adapters/HttpClient';
import { apiClientWH } from '@shared/services';
import { useInfiniteQuery } from '@tanstack/react-query';
import { ShipmentsApiAdapter } from '../adapters/ShipmentsApiAdapter';
import { Filters } from '../models/Filters';
import { ShipmentsService } from '../services/ShipmentsService';

export const useBlockedShipments = (filters: Filters = {}) => {
  const httpClient = createHttpClient(apiClientWH);
  const apiAdapter = new ShipmentsApiAdapter(httpClient);
  const service = new ShipmentsService(apiAdapter);

  const {
    data,
    isLoading,
    isFetching,
    error,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isPending,
  } = useInfiniteQuery({
    queryKey: ['blockedShipments', filters],
    queryFn: ({ pageParam = 1 }) =>
      service.getBlockedShipmentsPaginated({
        ...filters,
        page: pageParam,
      }),
    getNextPageParam: (lastPage) => lastPage.nextPage,
    initialPageParam: 1,
    staleTime: 2 * 60 * 1000, // 2 minutos
    // 🎯 En React Query v5, usamos placeholderData para mantener los datos anteriores
    placeholderData: (previousData) => previousData,
  });

  const blockedShipments = data?.pages.flatMap((page) => page.blockedShipments) ?? [];
  const totalFromApi = data?.pages?.[0]?.total ?? 0;

  return {
    blockedShipments,
    isLoading: isLoading || isPending,
    isFetching,
    error,
    refetch,
    fetchNextPage,
    hasNextPage: hasNextPage ?? false,
    isFetchingNextPage,
    totalPages: data?.pages.length ?? 0,
    totalLocations: blockedShipments.length,
    total: totalFromApi, // Total real del API (con filtros)
  };
};

